/**
 * "Report this issue" on a failed turn (1.33.0; ADR-131, Amendment 1).
 *
 * When a turn fails, a site that passed `statusReport` offers the visitor a
 * link that tells the status service in shared-support. The report names the
 * product and the component, the kind of failure, the HTTP status and the
 * page's path; nothing about the visitor, the message or the conversation.
 * The status service counts it on the product's public status page.
 *
 * Opt-in and snippet-only: the server configuration never carries it. The
 * status service knows PuglieseWeb's own products, so a client's site must
 * not report its visitors' failures there.
 */
import { httpsUrlOrEmpty } from './config';
import type { StatusReportOption } from './types';

export type FailureKind = 'http' | 'network' | 'timeout' | 'unknown';

/** Why a turn failed: what "Report this issue" sends. */
export interface TurnFailure {
  kind: FailureKind;
  /** Only for `http`: the status the API answered with. */
  httpStatus?: number;
}

/** A turn the widget knows the cause of: the API answered an error, or the stream broke. */
export class TurnError extends Error {
  readonly kind: FailureKind;
  readonly httpStatus?: number;
  constructor(kind: FailureKind, httpStatus?: number) {
    super(kind === 'http' ? `HTTP ${httpStatus}` : kind);
    this.name = 'TurnError';
    this.kind = kind;
    this.httpStatus = httpStatus;
  }
}

export function classifyFailure(err: unknown): TurnFailure {
  if (err instanceof TurnError) {
    return err.kind === 'http' && err.httpStatus !== undefined ? { kind: 'http', httpStatus: err.httpStatus } : { kind: err.kind };
  }
  // The only abort on a send is its own timer (MESSAGE_TIMEOUT_MS).
  if (err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError')) return { kind: 'timeout' };
  // fetch rejects with a TypeError when nothing answered: offline, DNS, a
  // CORS refusal, or the page's CSP blocking the host.
  if (err instanceof TypeError) return { kind: 'network' };
  return { kind: 'unknown' };
}

export type { StatusReportOption };

export interface StatusReport {
  apiUrl: string;
  product: string;
  pageUrl?: string;
  environment: 'prod' | 'preprod';
}

const PRODUCT_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** The option as the widget uses it, or null (with a warning) when it cannot be used. */
export function parseStatusReport(value: unknown): StatusReport | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'object' || Array.isArray(value)) {
    console.warn('[PweChat] Ignoring statusReport — expected an object like { apiUrl, product, pageUrl?, environment? }.');
    return null;
  }
  const o = value as Record<string, unknown>;
  const apiUrl = httpsUrlOrEmpty(o.apiUrl, 'statusReport.apiUrl', 'snippet').replace(/\/+$/, '');
  if (!apiUrl) {
    console.warn('[PweChat] Ignoring statusReport — it needs apiUrl, an absolute https:// URL.');
    return null;
  }
  if (typeof o.product !== 'string' || !PRODUCT_RE.test(o.product)) {
    console.warn(
      `[PweChat] Ignoring statusReport — product ${JSON.stringify(o.product)} is not a product name ` +
        '(lowercase letters, digits and -, as the status service names it).',
    );
    return null;
  }
  const pageUrl = httpsUrlOrEmpty(o.pageUrl, 'statusReport.pageUrl', 'snippet');
  return {
    apiUrl,
    product: o.product,
    ...(pageUrl ? { pageUrl } : {}),
    environment: o.environment === 'preprod' ? 'preprod' : 'prod',
  };
}

/** What `POST {apiUrl}/v1/reports` receives. `page` is a path: no query, no fragment. */
export function reportBody(report: StatusReport, failure: TurnFailure, pathname: string): Record<string, unknown> {
  return {
    product: report.product,
    component: 'assistant',
    kind: failure.kind,
    ...(failure.httpStatus !== undefined ? { httpStatus: failure.httpStatus } : {}),
    page: pathname.split('?')[0].split('#')[0],
    environment: report.environment,
  };
}

/** Sends the report; true when the status service recorded it. Never throws. */
export async function sendReport(report: StatusReport, failure: TurnFailure, pathname: string): Promise<boolean> {
  try {
    const res = await fetch(`${report.apiUrl}/v1/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reportBody(report, failure, pathname)),
    });
    return res.ok;
  } catch {
    return false;
  }
}
